"use strict";

import { gridDefaults } from "./gridDefaults.js";

const namespace = ".rs.jquery.bootgrid";

// GRID PUBLIC CLASS DEFINITION
// ====================

/**
 * Represents the jQuery Bootgrid plugin.
 *
 * @class Grid
 * @constructor
 * @param element {Object} The corresponding DOM element.
 * @param options {Object} The options to override default settings.
 * @chainable
 **/
class Grid {
    constructor(element, options) {
        this.element = $(element);
        this.origin = this.element.clone();
        this.options = $.extend(true, {}, gridDefaults, this.element.data(), options);
        // overrides rowCount explicitly because deep copy ($.extend) leads to strange behaviour
        const rowCount = (this.options.rowCount = this.element.data().rowCount || options.rowCount || this.options.rowCount);
        this.columns = [];
        this.current = this.options.current;
        this.currentRows = [];
        this.identifier = null; // The first column ID that is marked as identifier
        this.selection = false;
        this.converter = null; // The converter for the column that is marked as identifier
        this.rowCount = Array.isArray(rowCount) ? options.selectedRowCount : rowCount;
        this.rows = [];
        this.searchPhrase = "";
        this.preventInitialLoad = options.preventInitialLoad ?? false;
        //TWINVISION
        //ADDED columnSearchPhrases to support column specific filtering
        this.columnSearchCollapsed = this.options.twinvisionColumnSearchCollapsed;
        this.columnSearchPhrases = this.options.twinvisionColumnSearchValues || {};
        this.selectedRowIds = [];
        this.sortDictionary = {};
        this.total = 0;
        this.totalPages = 0;
        this.cachedParams = {
            lbl: this.options.labels,
            css: this.options.css,
            ctx: {},
        };
        this.header = null;
        this.footer = null;
        this.preventInitialPost = false;
        this.isInitialPost = true;
        // todo: implement cache
    }

    init() {
        this.element.trigger("initialize" + namespace);

        this.loadColumns(); // Loads columns from HTML thead tag
        this.loadRows(); // Loads rows from HTML tbody tag if ajax is false
        this.prepareTable();
        this.renderTableHeader();
        this.renderSearchField();
        this.renderActions();
        if (!this.preventInitialLoad) {
            this.loadData();
        }

        this.element.trigger("initialized" + namespace);
    }

    loadData(data = null) {
        this.element._bgBusyAria(true).trigger("load" + namespace);
        this.showLoading();

        const update = (rows, total) => {
            this.currentRows = rows;
            this.setTotals(total);

            if (!this.options.keepSelection) {
                this.selectedRowIds = [];
            }

            this.renderRows(rows);
            this.renderInfos();
            this.renderPagination();
            this.element._bgBusyAria(false).trigger("loaded" + namespace);
        };

        if (this.options.ajax) {
            
            const processResponse = (response) => {
                if (typeof response === "string") {
                    response = JSON.parse(response);
                }

                response = this.options.responseHandler(response);
                this.current = response.current;
                update(response.rows, response.total);
            };

            if (data !== null) {
                processResponse(data);
                return;
            }
            const getFormData = (object) =>
                Object.keys(object).reduce((formData, key) => {
                    const menuItemId = $("#" + object.id).data("luscinia-menuitem-id");
                    formData.append("menu-item-id", menuItemId);
                    formData.append(key, object[key]);
                    return formData;
                }, new FormData());
                
            let request = this.getRequest(),
                url = this.getUrl();

            if (url == null || typeof url !== "string" || url.length === 0) {
                throw new Error("Url setting must be a none empty string or a function that returns one.");
            }

            let settings = {
                url: url,
                data: getFormData(request),
                type: "POST",
                processData: false,
                contentType: false,
                success: (data) => {
                    processResponse(data);
                },
                error: (jqXHR, textStatus, errorThrown) => {
                    if (textStatus !== "abort") {
                        this.renderNoResultsRow(); // overrides loading mask
                        this.element._bgBusyAria(false).trigger("loaded" + namespace);
                    }
                },
            };
            settings = $.extend(this.options.ajaxSettings, settings);

            if (this.preventInitialPost && this.isInitialPost && !window.location.href.includes("Record?action=edit")) {
                this.isInitialPost = false;
                return;
            }
            $.ajax(settings);
        } else {
            var rows = (this.searchPhrase.length > 0) ? this.rows.where(containsPhrase) : this.rows,
            total = rows.length;
            
            if (this.rowCount !== -1)
            {
                rows = rows.page(this.current, this.rowCount);
            }

            // todo: improve the following comment
            // setTimeout decouples the initialization so that adding event handlers happens before
            window.setTimeout(function () { update(rows, total); }, 10);
        }
    }

    getObject() {
        return this;
    }

    getIdentifier() {
        return this.identifier;
    }

    /**
     * Appends rows.
     *
     * @method append
     * @param rows {Array} An array of rows to append
     * @chainable
     **/
    append(rows) {
        if (this.options.ajax) {
            throw new Error("Not yet implemented!");
        } else {
            let appendedRows = [];
            for (let i = 0; i < rows.length; i++) {
                if (this.appendRow(rows[i])) {
                    appendedRows.push(rows[i]);
                }
            }
            this.sortRows();
            this.loadData();
            this.element.trigger("appended" + namespace, [appendedRows]);
        }

        return this;
    }

    /**
     * Removes all rows.
     *
     * @method clear
     * @chainable
     **/
    clear() {
        if (this.options.ajax) {
            // todo: implement ajax POST
        } else {
            const removedRows = $.extend([], this.rows);
            this.rows = [];
            this.current = 1;
            this.total = 0;
            this.loadData();
            this.element.trigger("cleared" + namespace, [removedRows]);
        }

        return this;
    }

    /**
     * Removes the control functionality completely and transforms the current state to the initial HTML structure.
     *
     * @method destroy
     * @chainable
     **/
    destroy() {
        // todo: this method has to be optimized (the complete initial state must be restored)
        $(window).off(namespace);
        if (this.options.navigation & 1) {
            this.header.remove();
        }
        if (this.options.navigation & 2) {
            this.footer.remove();
        }
        this.element.before(this.origin).remove();

        return this;
    }

    /**
     * Resets the state and reloads rows.
     *
     * @method reload
     * @chainable
     **/
    reload() {
        this.options.onReload ? this.options.onReload() : this.element.trigger("reload");
        return this;
    }

    updateUrl(url = null) {
        this.options.url = url ?? this.element.attr("data-url");
        return this;
    }

    reloadWithData(data) {
        this.loadData(data);
        this.element.trigger("afterReloadWithData", this);
        return this;
    }

    /**
     * Removes rows by ids. Removes selected rows if no ids are provided.
     *
     * @method remove
     * @param [rowsIds] {Array} An array of rows ids to remove
     * @chainable
     **/
    remove(rowIds) {
        if (this.identifier != null) {
            if (this.options.ajax) {
                // todo: implement ajax DELETE
            } else {
                rowIds = rowIds || this.selectedRowIds;
                let id,
                    removedRows = [];

                for (let i = 0; i < rowIds.length; i++) {
                    id = rowIds[i];

                    for (let j = 0; j < this.rows.length; j++) {
                        if (this.rows[j][this.identifier] === id) {
                            removedRows.push(this.rows[j]);
                            this.rows.splice(j, 1);
                            break;
                        }
                    }
                }

                this.current = 1; // reset
                this.loadData();
                this.element.trigger("removed" + namespace, [removedRows]);
            }
        }

        return this;
    }

    /**
     * Searches in all rows for a specific phrase (but only in visible cells).
     * The search filter will be reseted, if no argument is provided.
     *
     * @method search
     * @param [phrase] {String} The phrase to search for
     * @chainable
     **/
    search(phrase, columnSearchPhrases) {
        phrase = phrase || "";
        columnSearchPhrases = columnSearchPhrases || {};

        if (this.searchPhrase !== phrase) {
            let selector = this.getCssSelector(this.options.css.searchField),
                searchFields = this.findFooterAndHeaderItems(selector);
            searchFields.val(phrase);
        }

        this.executeSearch(phrase, columnSearchPhrases);

        return this;
    }

    /**
     * Selects rows by ids. Selects all visible rows if no ids are provided.
     * In server-side scenarios only visible rows are selectable.
     *
     * @method select
     * @param [rowsIds] {Array} An array of rows ids to select
     * @chainable
     **/
    select(rowIds) {
        if (this.selection) {
            rowIds = rowIds || this.currentRows.propValues(this.identifier);

            let id,
                i,
                selectedRows = [];

            while (rowIds.length > 0 && !(!this.options.multiSelect && selectedRows.length === 1)) {
                id = rowIds.pop();
                if ($.inArray(id, this.selectedRowIds) === -1) {
                    for (i = 0; i < this.currentRows.length; i++) {
                        if (this.currentRows[i][this.identifier] === id) {
                            selectedRows.push(this.currentRows[i]);
                            this.selectedRowIds.push(id);
                            break;
                        }
                    }
                }
            }

            if (selectedRows.length > 0) {
                let selectBoxSelector = this.getCssSelector(this.options.css.selectBox),
                    selectMultiSelectBox = this.selectedRowIds.length >= this.currentRows.length;

                i = 0;
                while (!this.options.keepSelection && selectMultiSelectBox && i < this.currentRows.length) {
                    selectMultiSelectBox = $.inArray(this.currentRows[i++][this.identifier], this.selectedRowIds) !== -1;
                }
                this.element.find("thead " + selectBoxSelector).prop("checked", selectMultiSelectBox);

                if (!this.options.multiSelect) {
                    this.element.find("tbody > tr " + selectBoxSelector + ":checked").trigger("click" + namespace);
                }

                for (i = 0; i < this.selectedRowIds.length; i++) {
                    this.element
                        .find('tbody > tr[data-row-id="' + this.selectedRowIds[i] + '"]')
                        .addClass(this.options.css.selected)
                        ._bgAria("selected", "true")
                        .find(selectBoxSelector)
                        .prop("checked", true);
                }

                this.element.trigger("selected" + namespace, [selectedRows]);
            }
        }

        return this;
    }

    /**
     * Deselects rows by ids. Deselects all visible rows if no ids are provided.
     * In server-side scenarios only visible rows are deselectable.
     *
     * @method deselect
     * @param [rowsIds] {Array} An array of rows ids to deselect
     * @chainable
     **/
    deselect(rowIds) {
        if (this.selection) {
            rowIds = rowIds || this.currentRows.propValues(this.identifier);

            let id,
                i,
                pos,
                deselectedRows = [];

            while (rowIds.length > 0) {
                id = rowIds.pop();
                pos = $.inArray(id, this.selectedRowIds);
                if (pos !== -1) {
                    for (i = 0; i < this.currentRows.length; i++) {
                        if (this.currentRows[i][this.identifier] === id) {
                            deselectedRows.push(this.currentRows[i]);
                            this.selectedRowIds.splice(pos, 1);
                            break;
                        }
                    }
                }
            }

            if (deselectedRows.length > 0) {
                let selectBoxSelector = this.getCssSelector(this.options.css.selectBox);

                this.element.find("thead " + selectBoxSelector).prop("checked", false);
                for (i = 0; i < deselectedRows.length; i++) {
                    this.element
                        .find('tbody > tr[data-row-id="' + deselectedRows[i][this.identifier] + '"]')
                        .removeClass(this.options.css.selected)
                        ._bgAria("selected", "false")
                        .find(selectBoxSelector)
                        .prop("checked", false);
                }

                this.element.trigger("deselected" + namespace, [deselectedRows]);
            }
        }

        return this;
    }

    /**
     * Sorts the rows by a given sort descriptor dictionary.
     * The sort filter will be reseted, if no argument is provided.
     *
     * @method sort
     * @param [dictionary] {Object} A sort descriptor dictionary that contains the sort information
     * @chainable
     **/
    sort(dictionary) {
        let values = dictionary ? $.extend({}, dictionary) : {};

        if (values === this.sortDictionary) {
            return this;
        }

        this.sortDictionary = values;
        this.renderTableHeader();
        this.sortRows();
        this.loadData();

        return this;
    }

    /**
     * Gets a list of the column settings.
     * This method returns only for the first grid instance a value.
     * Therefore be sure that only one grid instance is catched by your selector.
     *
     * @method getColumnSettings
     * @return {Array} Returns a list of the column settings.
     * @since 1.2.0
     **/
    getColumnSettings() {
        return $.merge([], this.columns);
    }

    /**
     * Updates the column order based on the provided column settings.
     *
     * @method updateColumnOrder
     * @param columnSettings {Array} An array of column settings representing the new column order.
     * @since 1.2.0
     **/
    updateColumnOrder(columnSettings) {
        this.columns = columnSettings;
    }

    /**
     * Gets the current page index.
     * This method returns only for the first grid instance a value.
     * Therefore be sure that only one grid instance is catched by your selector.
     *
     * @method getCurrentPage
     * @return {Number} Returns the current page index.
     * @since 1.2.0
     **/
    getCurrentPage() {
        return this.current;
    }

    /**
     * Gets the current rows.
     * This method returns only for the first grid instance a value.
     * Therefore be sure that only one grid instance is catched by your selector.
     *
     * @method getCurrentPage
     * @return {Array} Returns the current rows.
     * @since 1.2.0
     **/
    getCurrentRows() {
        return $.merge([], this.currentRows);
    }
    getCurrentRowIds() {
        return $.merge([], this.currentRows.map(row => row[this.identifier]));
    }

    /**
     * Gets a number represents the row count per page.
     * This method returns only for the first grid instance a value.
     * Therefore be sure that only one grid instance is catched by your selector.
     *
     * @method getRowCount
     * @return {Number} Returns the row count per page.
     * @since 1.2.0
     **/
    getRowCount() {
        return this.rowCount;
    }

    setCurrentPage(pageNumber) {
        this.current = pageNumber;
    }

    getDisplayedRowCount() {
        return this.currentRows.length;
    }

    getTotalPagesCount() {
        return this.totalPages;
    }

    /**
     * Gets the actual search phrase.
     * This method returns only for the first grid instance a value.
     * Therefore be sure that only one grid instance is catched by your selector.
     *
     * @method getSearchPhrase
     * @return {String} Returns the actual search phrase.
     * @since 1.2.0
     **/
    getSearchPhrase() {
        return this.searchPhrase;
    }

    getSelectedRows() {
        return this.currentRows.where((row) => this.selectedRowIds.includes(row[this.identifier]));
    }



    /**
     * Gets the complete list of currently selected rows.
     * This method returns only for the first grid instance a value.
     * Therefore be sure that only one grid instance is catched by your selector.
     *
     * @method getSelectedRows
     * @return {Array} Returns all selected rows.
     * @since 1.2.0
     **/
    getSelectedRowIds() {
        return $.merge([], this.selectedRowIds);
    }

    /**
     * Gets the sort dictionary which represents the state of column sorting.
     * This method returns only for the first grid instance a value.
     * Therefore be sure that only one grid instance is catched by your selector.
     *
     * @method getSortDictionary
     * @return {Object} Returns the sort dictionary.
     * @since 1.2.0
     **/
    getSortDictionary() {
        return $.extend({}, this.sortDictionary);
    }

    /**
     * Gets a number represents the total page count.
     * This method returns only for the first grid instance a value.
     * Therefore be sure that only one grid instance is catched by your selector.
     *
     * @method getTotalPageCount
     * @return {Number} Returns the total page count.
     * @since 1.2.0
     **/
    getTotalPageCount() {
        return this.totalPages;
    }

    /**
     * Gets a number represents the total row count.
     * This method returns only for the first grid instance a value.
     * Therefore be sure that only one grid instance is catched by your selector.
     *
     * @method getTotalRowCount
     * @return {Number} Returns the total row count.
     * @since 1.2.0
     **/
    getTotalRowCount() {
        return this.total;
    }

    /**
     * TWINVISION
     * @return if column search is collapsed at this point time
     **/
    getColumnSearchCollapsed() {
        return this.columnSearchCollapsed;
    }

    /**
     * TWINVISION
     * @return get all column search phrases as an dictionary object [columnId,value]
     **/
    getColumnSearchPhrases() {
        return this.columnSearchPhrases;
    }

    // private stuff
    appendRow(row) {
        if (!this.rows.some((item) => this.identifier && item[this.identifier] === row[this.identifier])) {
            this.rows.push(row);
            return true;
        }
        return false;
    }

    findFooterAndHeaderItems(selector) {
        let footer = this.footer ? this.footer.find(selector) : $(),
            header = this.header ? this.header.find(selector) : $();
        return $.merge(footer, header);
    }

    getParams(context) {
        return context ? $.extend({}, this.cachedParams, { ctx: context }) : this.cachedParams;
    }

    getRequest() {
        let request = {
                current: this.current,
                rowCount: this.rowCount,
                sort: JSON.stringify(this.sortDictionary),
                searchPhrase: this.searchPhrase,
                columnSearchPhrases: JSON.stringify(this.columnSearchPhrases),
            },
            post = this.options.post;

        post = $.isFunction(post) ? post() : post;
        return this.options.requestHandler($.extend(true, request, post));
    }

    getCssSelector(css) {
        return "." + $.trim(css).replace(/\s+/gm, ".");
    }

    getUrl() {
        let url = this.options.url;
        return $.isFunction(url) ? url() : url;
    }

    isVisible(column) {
        return column.visible;
    }

    loadColumns() {
        let firstHeadRow = this.element.find("thead > tr").first(),
            sorted = false;

        firstHeadRow.children().each((index, element) => {
            let $element = $(element),
                data = $element.data(),
                column = {
                    id: data.columnId,
                    identifier: (this.identifier == null && data.identifier) || false,
                    converter: this.options.converters[data.converter || data.type] || this.options.converters["string"],
                    text: $element.text(),
                    align: data.align || "left",
                    headerAlign: data.headerAlign || "left",
                    cssClass: data.cssClass || "",
                    headerCssClass: data.headerCssClass || "",
                    formatter: this.options.formatters[data.formatter] || null,
                    dataType: data.type,
                    order: !sorted && (data.order === "asc" || data.order === "desc") ? data.order : null,
                    searchable: !(data.searchable === false), // default: true
                    sortable: !(data.sortable === false), // default: true
                    visible: !(data.visible === false), // default: true
                    width: $.isNumeric(data.width) ? data.width + "px" : typeof data.width === "string" ? data.width : null,
                    filterWidget: data.filterWidget || null,
                    filterOptionsUrl: data.filterOptionsUrl || null,
                };

            this.columns.push(column);

            if (column.order != null) {
                this.sortDictionary[column.id] = column.order;
            }

            // Prevents multiple identifiers
            if (column.identifier) {
                this.identifier = column.id;
                this.converter = column.converter;
            }

            // ensures that only the first order will be applied in case of multi sorting is disabled
            if (!this.options.multiSort && column.order !== null) {
                sorted = true;
            }
        });

        this.selection = this.options.selection && this.identifier != null;
    }

    initColumnSearchFields() {
        let tpl = this.options.templates,
            searchColumn = tpl.columnSearch.resolve(this.getParams());
        let newSearchPhrases = {};
        if (this.options.twinvisionColumnSearch) {
            $(this.element)
                .find("th[data-column-id]")
                .each((index, element) => {
                    let columnId = $(element).data("column-id");
                    if (columnId !== null && columnId !== undefined) {
                        if (!this.options.twinvisionColumnSearchExclusions.includes(columnId)) {
                            let columnSearchValue = this.columnSearchPhrases[columnId];
                            newSearchPhrases[columnId] = columnSearchValue;
                            let tempValue = "";
                            if (columnSearchValue !== null && columnSearchValue !== undefined) {
                                tempValue += columnSearchValue;
                            }
                            let $searchColumn = $(searchColumn);
                            $searchColumn.val(tempValue);
                            if (this.columnSearchCollapsed) {
                                $searchColumn.hide();
                            }
                            $(element).append($searchColumn);
                        }
                    }
                });
        }
        this.columnSearchPhrases = newSearchPhrases;
    }

    getURLParameters() {
        let urlParams = {};
        let url = window.location.href;
        let paramStartIndex = url.indexOf("?");

        if (paramStartIndex !== -1) {
            let paramString = url.substring(paramStartIndex + 1);
            let paramArray = paramString.split("&");

            for (let i = 0; i < paramArray.length; i++) {
                let param = paramArray[i].split("=");
                let paramName = decodeURIComponent(param[0]);
                let paramValue = decodeURIComponent(param[1] || "");

                if (paramName.length > 0) {
                    urlParams[paramName] = paramValue;
                }
            }
        }

        return urlParams;
    }

    loadRows() {
        if (!this.options.ajax) {
            const rows = this.element.find("tbody > tr");

            rows.each((index, element) => {
                const $currentElement = $(element),
                    cells = $currentElement.children("td"),
                    row = {};

                this.columns.forEach((column, i) => {
                    row[column.id] = column.converter.from(cells.eq(i).text());
                });

                this.appendRow(row);
            });

            this.setTotals(this.rows.length);
            this.sortRows();
        }
    }

    setTotals(total) {
        this.total = total;
        this.totalPages = this.rowCount === -1 ? 1 : Math.ceil(this.total / this.rowCount);
    }

    prepareTable() {
        let tpl = this.options.templates,
            wrapper = this.element.parent().hasClass(this.options.css.responsiveTable) ? this.element.parent() : this.element;

        this.element.addClass(this.options.css.table);

        // checks whether there is an tbody element; otherwise creates one
        if (this.element.children("tbody").length === 0) {
            this.element.append(tpl.body);
        }

        if (this.options.navigation & 1) {
            this.header = $(tpl.header.resolve(this.getParams({ id: this.element._bgId() + "-header" })));
            wrapper.before(this.header);
        }

        if (this.options.navigation & 2) {
            this.footer = $(tpl.footer.resolve(this.getParams({ id: this.element._bgId() + "-footer" })));
            wrapper.after(this.footer);
        }
    }

    renderActions() {
        if (this.options.navigation !== 0) {
            let css = this.options.css,
                selector = this.getCssSelector(css.actions),
                actionItems = this.findFooterAndHeaderItems(selector);

            if (actionItems.length > 0) {
                let tpl = this.options.templates,
                    actions = $(tpl.actions.resolve(this.getParams()));

                if (this.options.refreshIconVisible) {
                    // Refresh Button
                    if (this.options.ajax) {
                        let refreshIcon = tpl.icon.resolve(this.getParams({ iconCss: css.iconRefresh })),
                            refresh = $(tpl.actionButton.resolve(this.getParams({ content: refreshIcon, text: this.options.labels.refresh }))).on(
                                "click" + namespace,
                                (e) => {
                                    // todo: prevent multiple fast clicks (fast click detection)
                                    e.stopPropagation();
                                    this.reload();
                                }
                            );
                        actions.append(refresh);
                    }
                }

                // Row count selection
                this.renderRowCountSelection(actions);

                // Column selection
                if (this.options.columnSelectionVisible) {
                    this.renderColumnSelection(actions);
                }

                this.replacePlaceHolder(actionItems, actions);

                if (this.options.twinvisionColumnSearch) {
                    this.renderColumSearchToggleButton(css);
                }
            }
        }
    }

    renderColumSearchToggleButton(css) {
        let tpl = this.options.templates,
            columnSearchOptionHtml = tpl.columnSearchToggle.resolve(this.getParams());

        let searchIconCollapsedClass = css.iconColumnSearchToggleCollapsed;
        let searchIconUnCollapsedClass = css.iconColumnSearchToggleUncollapsed;
        let currentSearchIcon = searchIconUnCollapsedClass;
        if (this.columnSearchCollapsed) {
            currentSearchIcon = searchIconCollapsedClass;
        }

        let $columnSearchToggle = $(columnSearchOptionHtml);
        let $iconElement = $columnSearchToggle.find(this.getCssSelector(css.iconColumnSearchToggle));
        $iconElement.removeClass(css.iconColumnSearchToggle);
        $iconElement.addClass(currentSearchIcon);
        let columnDropDown = $(this.header).find(this.getCssSelector(css.iconColumns)).closest(".dropdown");
        columnDropDown.addClass("column-dropdown");

        if (columnDropDown.length === 0) {
            $(this.header).find(".actions").prepend($columnSearchToggle);
        } else {
            columnDropDown.before($columnSearchToggle);
        }

        $(this.header)
            .find(".search-collapse")
            .on("click", (e) => {
                const $element = $(e.currentTarget);
                if (this.columnSearchCollapsed) {
                    let icon = $element.find(this.getCssSelector(searchIconCollapsedClass));
                    icon.removeClass(searchIconCollapsedClass);
                    icon.addClass(searchIconUnCollapsedClass);
                    this.element.find(".columnsearchfield").show();
                } else {
                    let icon = $element.find(this.getCssSelector(searchIconUnCollapsedClass));
                    icon.removeClass(searchIconUnCollapsedClass);
                    icon.addClass(searchIconCollapsedClass);
                    this.element.find(".columnsearchfield").hide();
                }

                this.columnSearchCollapsed = !this.columnSearchCollapsed;
                this.element.trigger("columnSearchCollapsedChanged");
            });
    }

    setRows = function (rows) {
        this.rows = rows;
        this.currentRows = rows;
        this.setTotals(this.currentRows.length);
        var scrollTop = $(window).scrollTop();
        this.renderRows(this.rows);
        $(window).scrollTop(scrollTop);
        this.renderInfos();
        this.renderPagination();
        this.element._bgBusyAria(false).trigger("loaded" + namespace);
    };

    setColumn = function (rowIndex, columnHeader, newValue) {
        this.rows[rowIndex][columnHeader] = newValue;
        this.currentRows = this.rows;
        this.setTotals(this.currentRows.length);
        var scrollTop = $(window).scrollTop();
        this.renderRows(this.rows);
        $(window).scrollTop(scrollTop);
        this.renderInfos();
        this.renderPagination();
        this.element._bgBusyAria(false).trigger("loaded" + namespace);
    };

    renderColumnSelection(actions) {
        if (this.options.columnSelection && this.columns.length > 1) {
            let css = this.options.css,
                tpl = this.options.templates,
                icon = tpl.icon.resolve(this.getParams({ iconCss: css.iconColumns })),
                dropDown = $(tpl.actionDropDown.resolve(this.getParams({ content: icon }))),
                selector = this.getCssSelector(css.dropDownItem),
                checkboxSelector = this.getCssSelector(css.dropDownItemCheckbox),
                itemsSelector = this.getCssSelector(css.dropDownMenuItems);

            $.each(this.columns, (i, column) => {
                if (column.text != "") {
                    let item = $(
                        tpl.actionDropDownCheckboxItem.resolve(this.getParams({ name: column.id, label: column.text, checked: column.visible }))
                    ).on("click" + namespace, selector, (e) => {
                        e.stopPropagation();

                        if (e.target.type !== "checkbox") {
                            return;
                        }

                        this.options.onColumnSelectionChanged && this.options.onColumnSelectionChanged();

                        let $element = $(e.currentTarget),
                            checkbox = $element.find(checkboxSelector);
                        if (!checkbox.prop("disabled")) {
                            column.visible = checkbox.prop("checked");
                            let enable = this.columns.where(this.isVisible).length > 1;
                            $element
                                .parents(itemsSelector)
                                .find(selector + ":has(" + checkboxSelector + ":checked)")
                                ._bgEnableAria(enable)
                                .find(checkboxSelector)
                                ._bgEnableField(enable);

                            this.renderTableHeader();
                        }
                        this.element._bgBusyAria(false).trigger("headerRenderedAfterColumnSelectionChanged");
                    });
                    dropDown.find(this.getCssSelector(css.dropDownMenuItems)).append(item);
                }
            });
            actions.append(dropDown);
        }
    }

    renderInfos() {
        if (this.options.navigation !== 0) {
            let selector = this.getCssSelector(this.options.css.infos),
                infoItems = this.findFooterAndHeaderItems(selector);

            if (infoItems.length > 0) {
                let end = this.current * this.rowCount,
                    infos = $(
                        this.options.templates.infos.resolve(
                            this.getParams({
                                end: this.total === 0 || end === -1 || end > this.total ? this.total : end,
                                start: this.total === 0 ? 0 : end - this.rowCount + 1,
                                total: this.total,
                            })
                        )
                    );

                this.replacePlaceHolder(infoItems, infos);
            }
        }
    }

    renderNoResultsRow() {
        let tbody = this.element.children("tbody").first(),
            tpl = this.options.templates,
            count = this.columns.where(this.isVisible).length;

        if (this.selection) {
            count = count + 1;
        }
        tbody.html(tpl.noResults.resolve(this.getParams({ columns: count })));
    }

    renderPagination() {
        if (this.options.navigation !== 0) {
            let selector = this.getCssSelector(this.options.css.pagination),
                paginationItems = this.findFooterAndHeaderItems(selector)._bgShowAria(this.rowCount !== -1);

            if (this.rowCount !== -1 && paginationItems.length > 0) {
                let tpl = this.options.templates,
                    current = this.current,
                    totalPages = this.totalPages,
                    pagination = $(tpl.pagination.resolve(this.getParams())),
                    offsetRight = totalPages - current,
                    offsetLeft = (this.options.padding - current) * -1,
                    startWith =
                        offsetRight >= this.options.padding ? Math.max(offsetLeft, 1) : Math.max(offsetLeft - this.options.padding + offsetRight, 1),
                    maxCount = this.options.padding * 2 + 1,
                    count = totalPages >= maxCount ? maxCount : totalPages;

                this.renderPaginationItem(pagination, "first", "&laquo;", "menu-item first")._bgEnableAria(current > 1);
                this.renderPaginationItem(pagination, "prev", "&lt;", "menu-item prev")._bgEnableAria(current > 1);

                for (let i = 0; i < count; i++) {
                    let pos = i + startWith;
                    this.renderPaginationItem(pagination, pos, pos, "menu-item page-" + pos)
                        ._bgEnableAria()
                        ._bgSelectAria(pos === current);
                }

                if (count === 0) {
                    this.renderPaginationItem(pagination, 1, 1, "menu-item page-" + 1)
                        ._bgEnableAria(false)
                        ._bgSelectAria();
                }

                this.renderPaginationItem(pagination, "next", "&gt;", "menu-item next")._bgEnableAria(totalPages > current);
                this.renderPaginationItem(pagination, "last", "&raquo;", "menu-item last")._bgEnableAria(totalPages > current);

                this.replacePlaceHolder(paginationItems, pagination);
            }
        }
    }

    renderPaginationItem(list, uri, text, markerCss) {
        let tpl = this.options.templates,
            css = this.options.css,
            values = this.getParams({ css: markerCss, text: text, uri: "#" + uri }),
            item = $(tpl.paginationItem.resolve(values)).on("click" + namespace, this.getCssSelector(css.paginationButton), (e) => {
                e.stopPropagation();

                let $currentElement = $(e.currentTarget),
                    parent = $currentElement.parent();
                if (!parent.hasClass("active") && !parent.hasClass("disabled")) {
                    let commandList = {
                        first: 1,
                        prev: this.current - 1,
                        next: this.current + 1,
                        last: this.totalPages,
                    };
                    let command = $currentElement.attr("href").substr(1);
                    this.current = commandList[command] || +command; // + converts string to int
                    setTimeout(() => {
                        this.element._bgBusyAria(false).trigger("pageChange");
                    }, 300);
                }
                $currentElement.trigger("blur");
            });

        list.append(item);
        return item;
    }

    renderRowCountSelection(actions) {
        let rowCountList = this.options.rowCount;

        const getText = (value) => {
            return value === -1 ? this.options.labels.all : value;
        };

        if ($.isArray(rowCountList)) {
            let css = this.options.css,
                tpl = this.options.templates,
                dropDown = $(tpl.actionDropDown.resolve(this.getParams({ content: getText(this.rowCount) }))),
                menuSelector = this.getCssSelector(css.dropDownMenu),
                menuTextSelector = this.getCssSelector(css.dropDownMenuText),
                menuItemsSelector = this.getCssSelector(css.dropDownMenuItems),
                menuItemSelector = this.getCssSelector(css.dropDownItemButton);
            dropDown.attr("id", "rowcount-dropdown");
            $.each(rowCountList, (index, value) => {
                let item = $(tpl.actionDropDownItem.resolve(this.getParams({ text: getText(value), uri: "#" + value })))
                    ._bgSelectAria(value === this.rowCount)
                    .on("click" + namespace, menuItemSelector, (e) => {
                        e.preventDefault();

                        let $currentElement = $(e.currentTarget),
                            newRowCount = +$currentElement.attr("href").substr(1);
                        if (newRowCount !== this.rowCount) {
                            // todo: sophisticated solution needed for calculating which page is selected
                            this.current = 1; // this.rowCount === -1 ---> All
                            this.rowCount = newRowCount;
                            $currentElement
                                .parents(menuItemsSelector)
                                .children()
                                .each(function () {
                                    let $item = $(this),
                                        currentRowCount = +$item.find(menuItemSelector).attr("href").substr(1);
                                    $item._bgSelectAria(currentRowCount === newRowCount);
                                });
                            $currentElement.parents(menuSelector).find(menuTextSelector).text(getText(newRowCount));
                            this.element._bgBusyAria(false).trigger("rowCountChange");
                        }
                    });
                dropDown.find(menuItemsSelector).append(item);
            });
            actions.append(dropDown);
        }
    }

    renderRows(rows) {
        if (rows.length > 0) {
            function escapeHtmlAttr(str) {
                return String(str)
                    .replace(/&/g, "&amp;")
                    .replace(/"/g, "&quot;")
                    .replace(/'/g, "&#39;")
                    .replace(/</g, "&lt;")
                    .replace(/>/g, "&gt;");
            }

            let css = this.options.css,
                tpl = this.options.templates,
                tbody = this.element.children("tbody").first(),
                allRowsSelected = true,
                html = "";

            $.each(rows, (index, row) => {
                let cells = "",
                    rowAttr = ' data-row-id="' + (this.identifier == null ? index : row[this.identifier]) + '"',
                    rowCss = "";

                if (this.selection) {
                    let selected = $.inArray(row[this.identifier], this.selectedRowIds) !== -1,
                        selectBox = tpl.select.resolve(this.getParams({ type: "checkbox", value: row[this.identifier], checked: selected }));
                    cells += tpl.cell.resolve(this.getParams({ content: selectBox, css: css.selectCell }));
                    allRowsSelected = allRowsSelected && selected;
                    if (selected) {
                        rowCss += css.selected;
                        rowAttr += ' aria-selected="true"';
                    }
                }

                // add data attributes to row
                for (let key in row) {
                    if (row.hasOwnProperty(key) && key.startsWith('data-')) {
                        rowAttr += ' ' + key + '="' + row[key] + '"';
                    }
                }

                let status = row.status != null && this.options.statusMapping[row.status];
                if (status) {
                    rowCss += status;
                }

                $.each(this.columns, (j, column) => {
                    if (column.visible) {
                        let value = $.isFunction(column.formatter) ? column.formatter.call(this, column, row) : column.converter.to(row[column.id]),
                            cssClass = column.cssClass.length > 0 ? " " + column.cssClass : "";

                        //only show the first line of a cell if it contains a line break
                        let shouldSplit = false;
                        if (typeof value === "string") {
                            shouldSplit = value.includes("\r\n");
                        }
                        const fullText = value;
                        value = shouldSplit ? value.split("\r\n")[0] : value;

                        cells += tpl.cell.resolve(
                            this.getParams({
                                content: value == null || value === "" ? "&nbsp;" : value,
                                css: (column.align === "right" ? css.right : column.align === "center" ? css.center : css.left) + cssClass,
                                style: column.width == null ? "" : "width:" + column.width + ";",
                                data: shouldSplit ? 'data-full-text="' + fullText + '"' : "" +  ` data-cell-value="${escapeHtmlAttr(row[column.id])}" data-column-id="${escapeHtmlAttr(column.id)}"`,
                            })
                        );
                    }
                });

                if (rowCss.length > 0) {
                    rowAttr += ' class="' + rowCss + '"';
                }
                html += tpl.row.resolve(this.getParams({ attr: rowAttr, cells: cells }));
            });

            // sets or clears multi selectbox state
            this.element.find("thead " + this.getCssSelector(this.options.css.selectBox)).prop("checked", allRowsSelected);

            tbody.html(html);

            this.registerRowEvents(tbody);
        } else {
            this.renderNoResultsRow();
        }
    }

    registerRowEvents(tbody) {
        let selectBoxSelector = this.getCssSelector(this.options.css.selectBox);

        if (this.selection) {
            tbody.off("click" + namespace, selectBoxSelector).on("click" + namespace, selectBoxSelector, (e) => {
                e.stopPropagation();

                let $currentElement = $(e.currentTarget),
                    id = this.converter.from($currentElement.val());

                if ($currentElement.prop("checked")) {
                    this.select([id]);
                } else {
                    this.deselect([id]);
                }
            });
        }

        tbody.off("click" + namespace, "> tr").on("click" + namespace, "> tr", (e) => {
            e.stopPropagation();

            let $currentElement = $(e.currentTarget),
                clickedRowId = this.identifier == null ? $currentElement.data("row-id") : this.converter.from($currentElement.data("row-id") + ""),
                row =
                    this.identifier == null
                        ? this.currentRows[clickedRowId]
                        : this.currentRows.first((item) => {
                              return item[this.identifier] === clickedRowId;
                          });

            if (this.selection && this.options.rowSelect) {
                const isSelected = $currentElement.hasClass(this.options.css.selected);

                if (isSelected) {
                    if (this.options.alwaysHaveOneRowSelected && this.selectedRowIds.length === 1) return;

                    if (!e.ctrlKey) {
                        this.deselect(this.selectedRowIds.filter((id) => id !== clickedRowId));
                    } else {
                        this.deselect([clickedRowId]);
                    }
                } else {
                    let rowsToSelect = [clickedRowId];

                    if (e.shiftKey) {
                        const clickedRowIndex = this.currentRows.findIndex((el) => el[this.identifier] === clickedRowId);
                        const lastSelectedRowIndex = this.currentRows.findIndex(
                            (el) => el[this.identifier] === this.selectedRowIds[this.selectedRowIds.length - 1]
                        );

                        const startIndex = Math.min(lastSelectedRowIndex, clickedRowIndex);
                        const endIndex = Math.max(lastSelectedRowIndex, clickedRowIndex);
                        rowsToSelect = this.currentRows.slice(startIndex, endIndex + 1).map((row) => row[this.identifier]);
                    }

                    if (!e.ctrlKey) {
                        this.deselect();
                    }

                    this.select(rowsToSelect);

                    if (e.shiftKey) {
                        if (window.getSelection) {
                            const selection = window.getSelection();
                            if (selection.rangeCount > 0) {
                                selection.removeAllRanges();
                            }
                        }
                    }
                }
            }
            this.options.onRowClicked?.([this.columns, row])
            this.element.trigger("click" + namespace, [this.columns, row]);
        });

        tbody.find("td[data-full-text]").each((index, element) => {
            const $currentElement = $(element);
            const fullText = $currentElement.data("full-text");
            const currentText = $currentElement.text() + " ...";
            $currentElement
                .on("mouseover", (e) => {
                    e.stopPropagation();
                    $currentElement.text(fullText);
                })
                .on("mouseout", (e) => {
                    e.stopPropagation();
                    $currentElement.text(currentText);
                });
        });
    }

    renderSearchField() {
        if (this.options.navigation !== 0) {
            let css = this.options.css,
                selector = this.getCssSelector(css.search),
                searchItems = this.findFooterAndHeaderItems(selector);

            if (searchItems.length > 0) {
                let tpl = this.options.templates,
                    timer = null, // fast keyup detection
                    currentValue = "",
                    searchFieldSelector = this.getCssSelector(css.searchField),
                    search = $(tpl.search.resolve(this.getParams())),
                    searchField = search.is(searchFieldSelector) ? search : search.find(searchFieldSelector);

                searchField.on("keyup" + namespace, (e) => {
                    e.stopPropagation();
                    
                    let newValue = $(e.currentTarget).val();
                    if (currentValue !== newValue || (e.which === 13 && newValue !== "")) {
                        currentValue = newValue;
                        if (e.which === 13 || newValue.length === 0 || newValue.length >= this.options.searchSettings.characters) {
                            window.clearTimeout(timer);
                            timer = window.setTimeout(() => {
                                this.executeSearch(newValue, this.columnSearchPhrases);
                            }, this.options.searchSettings.delay);
                        }
                    }
                });

                this.replacePlaceHolder(searchItems, search);
            }
        }
    }

    executeSearch(phrase, columnSearchPhrases) {
        if (this.searchPhrase !== phrase || this.columnSearchPhrases !== columnSearchPhrases) {
            this.current = 1;
            this.searchPhrase = phrase;
            this.columnSearchPhrases = columnSearchPhrases;

            this.reload();
        }
    }

    renderTableHeader() {
        let headerRow = this.element.find("thead > tr"),
            css = this.options.css,
            tpl = this.options.templates,
            html = "",
            sorting = this.options.sorting;

        if (this.selection) {
            let selectBox = this.options.isSelectAllCheckboxVisible && this.options.multiSelect ? tpl.select.resolve(this.getParams({ type: "checkbox", value: "all" })) : "";
            html += tpl.rawHeaderCell.resolve(
                this.getParams({
                    content: selectBox,
                    css: css.selectCell,
                })
            );
        }

        $.each(this.columns, (index, column) => {
            if (column.visible) {
                let sortOrder = this.sortDictionary[column.id],
                    iconCss =
                        sorting && sortOrder && sortOrder === "asc" ? css.iconUp : sorting && sortOrder && sortOrder === "desc" ? css.iconDown : "",
                    icon = tpl.icon.resolve(this.getParams({ iconCss: iconCss })),
                    align = column.headerAlign,
                    cssClass = column.headerCssClass.length > 0 ? " " + column.headerCssClass : "";
                html += tpl.headerCell.resolve(
                    this.getParams({
                        column: column,
                        icon: icon,
                        sortable: (sorting && column.sortable && css.sortable) || "",
                        css: (align === "right" ? css.right : align === "center" ? css.center : css.left) + cssClass,
                        style: column.width == null ? "" : "width:" + column.width + ";",
                    })
                );
            }
        });

        headerRow.html(html);

        // todo: create a own function for that piece of code
        if (this.selection && this.options.multiSelect) {
            let selectBoxSelector = this.getCssSelector(css.selectBox);
            headerRow.off("click" + namespace, selectBoxSelector).on("click" + namespace, selectBoxSelector, (e) => {
                e.stopPropagation();

                if ($(e.currentTarget).prop("checked")) {
                    this.select();
                } else {
                    this.deselect();
                }
            });
        }

        this.initColumnSearchFields();
    }

    setTableHeaderSortDirection(element) {
        let css = this.options.css,
            iconSelector = this.getCssSelector(css.icon),
            columnId = element.data("column-id") || element.parents("th").first().data("column-id"),
            sortOrder = this.sortDictionary[columnId],
            icon = element.find(iconSelector);

        if (!this.options.multiSort) {
            element
                .parents("tr")
                .first()
                .find(iconSelector)
                .removeClass(css.iconDown + " " + css.iconUp);
            this.sortDictionary = {};
        }

        if (sortOrder && sortOrder === "asc") {
            this.sortDictionary[columnId] = "desc";
            icon.removeClass(css.iconUp).addClass(css.iconDown);
        } else if (sortOrder && sortOrder === "desc") {
            if (this.options.multiSort) {
                let newSort = {};
                for (let key in this.sortDictionary) {
                    if (key !== columnId) {
                        newSort[key] = this.sortDictionary[key];
                    }
                }
                this.sortDictionary = newSort;
                icon.removeClass(css.iconDown);
            } else {
                this.sortDictionary[columnId] = "asc";
                icon.removeClass(css.iconDown).addClass(css.iconUp);
            }
        } else {
            this.sortDictionary[columnId] = "asc";
            icon.addClass(css.iconUp);
        }
    }

    replacePlaceHolder(placeholder, element) {
        placeholder.each((index, item) => {
            // todo: check how append is implemented. Perhaps cloning here is superfluous.
            $(item).before(element.clone(true)).remove();
        });
    }

    showLoading() {
        window.setTimeout(() => {
            if (this.element._bgAria("busy") === "true") {
                let tpl = this.options.templates,
                    thead = this.element.children("thead").first(),
                    tbody = this.element.children("tbody").first(),
                    firstCell = tbody.find("tr > td").first(),
                    padding = this.element.height() - thead.height() - (firstCell.height() + 20),
                    count = this.columns.where(this.isVisible).length;

                if (this.selection) {
                    count = count + 1;
                }
                tbody.html(tpl.loading.resolve(this.getParams({ columns: count })));
                if (this.rowCount !== -1 && padding > 0) {
                    tbody.find("tr > td").css("padding", "20px 0 " + padding + "px");
                }
            }
        }, 250);
    }

    sortRows() {
        let sortArray = [];

        function sort(x, y, current) {
            current = current || 0;
            let next = current + 1,
                item = sortArray[current];

            function sortOrder(value) {
                return item.order === "asc" ? value : value * -1;
            }

            let comparison = $.isFunction(item.converter?.sort)
                ? item.converter.sort(x[item.id], y[item.id])
                : x[item.id] > y[item.id]
                  ? 1
                  : x[item.id] < y[item.id]
                    ? -1
                    : 0;

            return comparison !== 0 ? sortOrder(comparison) : sortArray.length > next ? sort(x, y, next) : 0;
        }

        if (!this.options.ajax) {
            for (let key in this.sortDictionary) {
                if (this.options.multiSort || sortArray.length === 0) {
                    sortArray.push({
                        id: key,
                        order: this.sortDictionary[key],
                        converter: this.columns.find((column) => column.id === key)?.converter,
                    });
                }
            }

            if (sortArray.length > 0) {
                this.rows.sort(sort);
            }
        }
    }
}

export function initializeBootgridPlugin() {
    $.fn.extend({
        _bgAria: function (name, value) {
            return value ? this.attr("aria-" + name, value) : this.attr("aria-" + name);
        },

        _bgBusyAria: function (busy) {
            return busy == null || busy ? this._bgAria("busy", "true") : this._bgAria("busy", "false");
        },

        _bgRemoveAria: function (name) {
            return this.removeAttr("aria-" + name);
        },

        _bgEnableAria: function (enable) {
            return enable == null || enable
                ? this.removeClass("disabled")._bgAria("disabled", "false")
                : this.addClass("disabled")._bgAria("disabled", "true");
        },

        _bgEnableField: function (enable) {
            return enable == null || enable ? this.removeAttr("disabled") : this.attr("disabled", "disable");
        },

        _bgShowAria: function (show) {
            return show == null || show ? this.show()._bgAria("hidden", "false") : this.hide()._bgAria("hidden", "true");
        },

        _bgSelectAria: function (select) {
            return select == null || select
                ? this.addClass("active")._bgAria("selected", "true")
                : this.removeClass("active")._bgAria("selected", "false");
        },

        _bgId: function (id) {
            return id ? this.attr("id", id) : this.attr("id");
        },
    });

    if (!String.prototype.resolve) {
        let formatter = {
            checked: function (value) {
                if (typeof value === "boolean") {
                    return value ? 'checked="checked"' : "";
                }
                return value;
            },
        };

        String.prototype.resolve = function (substitutes, prefixes) {
            let result = this;
            $.each(substitutes, function (key, value) {
                if (value != null && typeof value !== "function") {
                    if (typeof value === "object") {
                        let keys = prefixes ? $.extend([], prefixes) : [];
                        keys.push(key);
                        result = result.resolve(value, keys) + "";
                    } else {
                        if (formatter && formatter[key] && typeof formatter[key] === "function") {
                            value = formatter[key](value);
                        }
                        key = prefixes ? prefixes.join(".") + "." + key : key;
                        let pattern = new RegExp("\\{\\{" + key + "\\}\\}", "gm");
                        result = result.replace(pattern, value.replace ? value.replace(/\$/gi, "&#36;") : value);
                    }
                }
            });
            return result;
        };
    }

    if (!Array.prototype.first) {
        Array.prototype.first = function (condition) {
            for (let i = 0; i < this.length; i++) {
                let item = this[i];
                if (condition(item)) {
                    return item;
                }
            }
            return null;
        };
    }

    if (!Array.prototype.contains) {
        Array.prototype.contains = function (condition) {
            for (let i = 0; i < this.length; i++) {
                let item = this[i];
                if (condition(item)) {
                    return true;
                }
            }
            return false;
        };
    }

    if (!Array.prototype.page) {
        Array.prototype.page = function (page, size) {
            let skip = (page - 1) * size,
                end = skip + size;
            return this.length > skip ? (this.length > end ? this.slice(skip, end) : this.slice(skip)) : [];
        };
    }

    if (!Array.prototype.where) {
        Array.prototype.where = function (condition) {
            let result = [];
            for (let i = 0; i < this.length; i++) {
                let item = this[i];
                if (condition(item)) {
                    result.push(item);
                }
            }
            return result;
        };
    }

    if (!Array.prototype.propValues) {
        Array.prototype.propValues = function (propName) {
            let result = [];
            for (let i = 0; i < this.length; i++) {
                result.push(this[i][propName]);
            }
            return result;
        };
    }

    // GRID PLUGIN DEFINITION
    // =====================

    let old = $.fn.bootgrid;

    $.fn.bootgrid = function (option) {
        let args = Array.prototype.slice.call(arguments, 1),
            returnValue = null,
            elements = this.each((index, element) => {
                let $currentElement = $(element),
                    instance = $currentElement.data(namespace),
                    options = typeof option === "object" && option;

                if (!instance && option === "destroy") {
                    return;
                }
                if (!instance) {
                    $currentElement.data(namespace, (instance = new Grid(this, options)));
                    instance.init();
                }
                if (typeof option === "string") {
                    if (option.indexOf("get") === 0 && index === 0) {
                        returnValue = instance[option].apply(instance, args);
                    } else if (option.indexOf("get") !== 0) {
                        return instance[option].apply(instance, args);
                    }
                }
            });
        return typeof option === "string" && option.indexOf("get") === 0 ? returnValue : elements;
    };

    $.fn.bootgrid.Constructor = Grid;

    // GRID NO CONFLICT
    // ===============

    $.fn.bootgrid.noConflict = function () {
        $.fn.bootgrid = old;
        return this;
    };

    // GRID DATA-API
    // ============

    $('[data-toggle="bootgrid"]').bootgrid();
}